import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import axios from '@/libs/axios'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { getStatusVariant, getStatusHistoryEntries, getLastStatusEntryByState, getUserNameFromEntry, getUserEmailFromEntry, getHistoryDate, getStatusNameFromEntry } from './useWorkflow'

const BASE_PATH = 'v1/policy-exceptions'

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()

  const tableColumns = [
    { label: 'policy_exception.reference_number', key: 'reference_number', sortable: true },
    { label: 'policy_exception.scope_type', key: 'scope_type.name', sortable: false },
    { label: 'policy_exception.coverage', key: 'coverage_info', sortable: false },
    { label: 'policy_exception.exception_type', key: 'exception_type.name', sortable: false },
    { label: 'policy_exception.status', key: 'status.name', sortable: false },
    { label: 'policy_exception.request_type', key: 'request_type.name', sortable: false },
    { label: 'policy_exception.assigned_to', key: 'assignedTo.name', sortable: false },
    { label: 'policy_exception.in_review', key: 'in_review_info', sortable: false },
    { label: 'policy_exception.approved_canceled', key: 'approved_canceled_info', sortable: false },
    { label: 'policy_exception.request_date', key: 'request_date', sortable: true, formatter: 'formatDate' },
    { label: 'policy_exception.start_date', key: 'start_date', sortable: true, formatter: 'formatDate' },
    { label: 'policy_exception.end_date', key: 'end_date', sortable: true, formatter: 'formatDate' },
  ]

  const getAssignedToDisplay = (item) => {
    const history = getStatusHistoryEntries(item)
    const inProgressEntry = getLastStatusEntryByState(history, 'in_progress')
    const reassignedEntry = getLastStatusEntryByState(history, 'reassigned')
    const getDate = (e) => e && (e.changed_at || e.created_at)
    const entry = [inProgressEntry, reassignedEntry]
      .filter(Boolean)
      .sort((a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0))[0]
    if (!entry) {
      const assigned = (item && item.assignedTo) || (item && item.assigned_to)
      return (assigned && assigned.name) || '-'
    }
    const name = getUserNameFromEntry(entry)
    const email = getUserEmailFromEntry(entry)
    return name || email || '-'
  }

  const getInReviewDisplay = (item) => {
    const history = getStatusHistoryEntries(item)
    const inReviewEntry = getLastStatusEntryByState(history, 'in_review')
    if (!inReviewEntry) return '-'
    const name = getUserNameFromEntry(inReviewEntry)
    const email = getUserEmailFromEntry(inReviewEntry)
    return name || email || '-'
  }

  const getApprovedCanceledDisplay = (item) => {
    const history = getStatusHistoryEntries(item)
    const approvedEntry = getLastStatusEntryByState(history, 'approved')
    const rejectedEntry = getLastStatusEntryByState(history, 'rejected')
    const entry = approvedEntry || rejectedEntry
    if (!entry) return '-'
    const name = getUserNameFromEntry(entry)
    const email = getUserEmailFromEntry(entry)
    return name || email || '-'
  }

  const getCoverageDisplay = (item) => {
    const scopeName = (item && item.scope_type && item.scope_type.name)
      ? String(item.scope_type.name).toLowerCase()
      : ''
    if (scopeName.includes('region')) {
      const regions = Array.isArray(item.regions) ? item.regions : []
      const names = regions.map((r) => (r && r.sector && r.sector.name) || '').filter(Boolean)
      return names.length ? names.join(', ') : '-'
    }
    if (scopeName.includes('inmueble')) {
      const units = Array.isArray(item.units) ? item.units : []
      const descriptions = units.map((u) => (u && u.unit && u.unit.description) || '').filter(Boolean)
      return descriptions.length ? descriptions.join(', ') : '-'
    }
    if (scopeName.includes('nacional')) return 'México'
    return '-'
  }

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str || ''
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  const getScopeTypeDisplay = (item) => {
    const scopeType = item && item.scope_type
    const name = scopeType && scopeType.name != null ? String(scopeType.name) : ''
    return name ? capitalizeFirst(name.replace(/_/g, ' ')) : '-'
  }

  const getExceptionTypeDisplay = (item) => {
    const exceptionType = item && item.exception_type
    const name = exceptionType && exceptionType.name != null ? String(exceptionType.name) : ''
    if (!name) return '-'
    const normalized = name.replace(/_/g, ' ')
    return capitalizeFirst(normalized)
  }

  if (ability.can('update', 'PolicyException') || ability.can('delete', 'PolicyException')) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref(null)
  const isSortDirDesc = ref(true)

  const statusFilter = ref(null)
  const scopeTypeFilter = ref(null)
  const requestTypeFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const unitFilter = ref(null)
  const sectorIdsFilter = ref([])
  const exceptionTypeFilter = ref(null)
  const standardIdsFilter = ref([])
  const assignedToFilter = ref(null)
  const reviewedByFilter = ref(null)
  const approvedByFilter = ref(null)
  const createdFromFilter = ref(null)
  const createdToFilter = ref(null)
  const validityFromFilter = ref(null)
  const validityToFilter = ref(null)

  const filters = ref({
    status_id: null,
    scope_type_id: null,
    request_type_id: null,
    unit_id: null,
    sector_ids: [],
    exception_type_id: null,
    standard_ids: [],
    assigned_to: null,
    reviewed_by: null,
    approved_by: null,
    created_from: null,
    created_to: null,
    validity_from: null,
    validity_to: null,
  })
  const filtersCount = useFilterCounter(filters)

  store.dispatch('unit/getAll')
  store.dispatch('region/getAll')
  store.dispatch('sector/getAll')
  store.dispatch('municipality/getAll')
  store.dispatch('user/getAll')
  store.dispatch('policyException/fetchCatalogs')
  store.dispatch('policyException/fetchStandards')

  const unitOptions = computed(() => store.getters['unit/getSelectedItems'] || [])
  const statusOptions = computed(() => store.getters['policyException/getStatuses'] || [])
  const scopeTypeOptions = computed(() => store.getters['policyException/getScopeTypes'] || [])
  const requestTypeOptions = computed(() => store.getters['policyException/getRequestTypes'] || [])
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems'] || [])
  const municipalityOptions = computed(() => store.getters['municipality/getSelectedItems'] || [])
  const userOptions = computed(() => {
    const items = store.getters['user/getItems'] || []
    return items.map((user) => {
      const email = user.email || ''
      const username = user.user || user.username || ''
      const name = [email, username].filter(Boolean).join(' | ') || String(user.id)
      return { id: user.id, name, value: user.id }
    })
  })
  const exceptionTypeOptions = computed(() => store.getters['policyException/getExceptionTypes'] || [])
  const standardOptions = computed(() => store.getters['policyException/getStandards'] || [])

  function buildQueryParams() {
    const base = {
      page: currentPage.value,
      per_page: perPage.value,
    }
    const sortField = sortBy.value
    if (sortField != null && String(sortField).trim() !== '') {
      base.sort_by = sortField
      base.sort_desc = isSortDirDesc.value ? 1 : 0
    }
    if (searchQuery.value != null && searchQuery.value !== '') {
      base.search = searchQuery.value
    }
    if (statusFilter.value != null && statusFilter.value !== '') {
      base.status_id = statusFilter.value
    }
    if (scopeTypeFilter.value != null && scopeTypeFilter.value !== '') {
      base.scope_type_id = scopeTypeFilter.value
    }
    if (requestTypeFilter.value != null && requestTypeFilter.value !== '') {
      base.request_type_id = requestTypeFilter.value
    }
    if (unitFilter.value != null && unitFilter.value !== '') {
      base.unit_id = unitFilter.value
    }
    if (scopeTypeFilter.value === 2 || scopeTypeFilter.value === '2') {
      const sectorIds = Array.isArray(sectorIdsFilter.value) ? sectorIdsFilter.value : []
      if (sectorIds.length > 0) {
        base['sector_ids[]'] = sectorIds
      }
    }
    if (exceptionTypeFilter.value != null && exceptionTypeFilter.value !== '') {
      base.exception_type_id = exceptionTypeFilter.value
    }
    const standardIds = Array.isArray(standardIdsFilter.value) ? standardIdsFilter.value : []
    if (standardIds.length > 0) {
      base['standard_ids[]'] = standardIds
    }
    if (assignedToFilter.value != null && assignedToFilter.value !== '') {
      base.assigned_to = assignedToFilter.value
    }
    if (reviewedByFilter.value != null && reviewedByFilter.value !== '') {
      base.reviewed_by = reviewedByFilter.value
    }
    if (approvedByFilter.value != null && approvedByFilter.value !== '') {
      base.approved_by = approvedByFilter.value
    }
    const createdFromRaw = createdFromFilter.value
    const createdToRaw = createdToFilter.value
    if (createdFromRaw != null && createdFromRaw !== '') {
      base.created_from = String(createdFromRaw).slice(0, 10)
    }
    if (createdToRaw != null && createdToRaw !== '' && (!createdFromRaw || String(createdFromRaw).slice(0, 10) <= String(createdToRaw).slice(0, 10))) {
      base.created_to = String(createdToRaw).slice(0, 10)
    }
    const validityFromRaw = validityFromFilter.value
    const validityToRaw = validityToFilter.value
    if (validityFromRaw != null && validityFromRaw !== '') {
      base.validity_from = String(validityFromRaw).slice(0, 10)
    }
    if (validityToRaw != null && validityToRaw !== '' && (!validityFromRaw || String(validityFromRaw).slice(0, 10) <= String(validityToRaw).slice(0, 10))) {
      base.validity_to = String(validityToRaw).slice(0, 10)
    }
    return base
  }

  const fetchList = () => {
    return new Promise((resolve, reject) => {
      const params = buildQueryParams()

      axios
        .get(BASE_PATH, { params })
        .then((response) => {
          const data = response.data
          let items = []
          if (Array.isArray(data.data)) {
            items = data.data
          } else if (data.data && Array.isArray(data.data.data)) {
            items = data.data.data
          } else if (data.data && typeof data.data === 'object') {
            items = [data.data]
          }
          let meta = {}
          if (data.meta != null) meta = data.meta
          else if (data.data && data.data.meta != null) meta = data.data.meta
          const total = meta.total != null ? meta.total : (data.total != null ? data.total : items.length)
          totalList.value = total
          resolve(items)
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error loading policy exceptions',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          reject(error)
        })
    })
  }

  const formatDate = (value) => {
    if (!value) return '-'
    return moment(value).format('DD/MM/YYYY')
  }

  const refetchData = () => {
    if (refListTable.value) refListTable.value.refresh()
  }

  const buildFilters = () => {
    filters.value = {
      status_id: statusFilter.value,
      scope_type_id: scopeTypeFilter.value,
      request_type_id: requestTypeFilter.value,
      unit_id: unitFilter.value,
      sector_ids: Array.isArray(sectorIdsFilter.value) ? [...sectorIdsFilter.value] : [],
      exception_type_id: exceptionTypeFilter.value,
      standard_ids: Array.isArray(standardIdsFilter.value) ? [...standardIdsFilter.value] : [],
      assigned_to: assignedToFilter.value,
      reviewed_by: reviewedByFilter.value,
      approved_by: approvedByFilter.value,
      created_from: createdFromFilter.value,
      created_to: createdToFilter.value,
      validity_from: validityFromFilter.value,
      validity_to: validityToFilter.value,
    }
    currentPage.value = 1
    refetchData()
  }

  const clearSearchFilters = () => {
    statusFilter.value = null
    scopeTypeFilter.value = null
    requestTypeFilter.value = null
    unitFilter.value = null
    sectorIdsFilter.value = []
    exceptionTypeFilter.value = null
    standardIdsFilter.value = []
    assignedToFilter.value = null
    reviewedByFilter.value = null
    approvedByFilter.value = null
    createdFromFilter.value = null
    createdToFilter.value = null
    validityFromFilter.value = null
    validityToFilter.value = null
    filters.value = {
      status_id: null,
      scope_type_id: null,
      request_type_id: null,
      unit_id: null,
      sector_ids: [],
      exception_type_id: null,
      standard_ids: [],
      assigned_to: null,
      reviewed_by: null,
      approved_by: null,
      created_from: null,
      created_to: null,
      validity_from: null,
      validity_to: null,
    }
    currentPage.value = 1
    refetchData()
  }

  watch([perPage], () => refetchData())
  watch([searchQuery], debounce(refetchData, 500))

  return {
    tableColumns,
    getAssignedToDisplay,
    getInReviewDisplay,
    getApprovedCanceledDisplay,
    getCoverageDisplay,
    getScopeTypeDisplay,
    getExceptionTypeDisplay,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    formatDate,
    getStatusVariant,
    statusFilter,
    scopeTypeFilter,
    requestTypeFilter,
    unitFilter,
    sectorIdsFilter,
    exceptionTypeFilter,
    standardIdsFilter,
    assignedToFilter,
    reviewedByFilter,
    approvedByFilter,
    createdFromFilter,
    createdToFilter,
    validityFromFilter,
    validityToFilter,
    filters,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    unitOptions,
    sectorOptions,
    municipalityOptions,
    userOptions,
    statusOptions,
    scopeTypeOptions,
    requestTypeOptions,
    exceptionTypeOptions,
    standardOptions,
  }
}
